/**
 * Access Request Helpers
 * 
 * Functions for managing access requests (free access request system)
 * Users can request access to private jobs, owners can approve/deny
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp, 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { createAccessGrant } from './accessGrantHelpers';

/**
 * Create an access request
 * 
 * @param {Object} requestData - Request configuration
 * @param {string} requestData.requesterId - Who is requesting
 * @param {string} requestData.jobId - Job they want access to
 * @param {string} requestData.evidenceId - Associated evidence
 * @param {string} requestData.casefileId - Source casefile
 * @param {string} requestData.ownerId - Job owner
 * @param {string} requestData.message - Why they need access
 * @param {string} requestData.reason - Category (investigation, research, legal, other)
 * @param {Object} [requestData.requesterInfo] - Cached requester info
 * @param {Object} [requestData.jobInfo] - Cached job info
 * @returns {Promise<string>} Request ID
 */
export const createAccessRequest = async (requestData) => {
  const {
    requesterId,
    jobId,
    evidenceId,
    casefileId,
    ownerId,
    message,
    reason,
    requesterInfo = {},
    jobInfo = {}
  } = requestData;

  // Validate required fields
  if (!requesterId || !jobId || !ownerId || !message) {
    throw new Error('Missing required fields for access request');
  }

  // Validate message length
  if (message.trim().length < 10) {
    throw new Error('Message must be at least 10 characters');
  }

  // Check if requester is the owner
  if (requesterId === ownerId) {
    throw new Error('You already own this job');
  }

  // Check for existing active access grant
  const grantId = `${requesterId}_${jobId}`;
  const existingGrant = await getDoc(doc(db, 'accessGrants', grantId));
  
  if (existingGrant.exists() && existingGrant.data().isActive) {
    throw new Error('You already have access to this job');
  }

  // Check for existing pending request
  const existingRequestQuery = query(
    collection(db, 'accessRequests'),
    where('requesterId', '==', requesterId),
    where('jobId', '==', jobId),
    where('status', '==', 'pending')
  );
  
  const existingRequestSnapshot = await getDocs(existingRequestQuery);
  
  if (!existingRequestSnapshot.empty) {
    throw new Error('You already have a pending request for this job');
  }

  // Create request ID
  const requestId = `req_${Date.now()}_${requesterId}`;

  // Calculate expiration (30 days from now)
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  // Build request document
  const request = {
    id: requestId,
    
    // Parties
    requesterId,
    ownerId,
    
    // Target
    jobId,
    evidenceId,
    casefileId,
    
    // Request details
    message: message.trim(),
    reason: reason || 'other',
    
    // Status
    status: 'pending',
    
    // Timestamps
    createdAt: serverTimestamp(),
    respondedAt: null,
    expiresAt,
    
    // Response
    ownerResponse: null,
    grantId: null,
    
    // Metadata (cached for display)
    requesterEmail: requesterInfo.email || '',
    requesterName: requesterInfo.name || 'Anonymous User',
    jobTitle: jobInfo.title || 'AI Analysis',
    casefileName: jobInfo.casefileName || 'Private Investigation'
  };

  // Save to database
  try {
    await setDoc(doc(db, 'accessRequests', requestId), request);
    console.log(`✅ Access request created: ${requestId}`);
    return requestId;
  } catch (error) {
    console.error('Error creating access request:', error);
    throw error;
  }
};

/**
 * Respond to an access request (approve or deny)
 * 
 * @param {string} requestId - Request to respond to
 * @param {string} action - 'approve' or 'deny'
 * @param {string} responderId - Who is responding (must be owner)
 * @param {string} [ownerResponse] - Optional message to requester
 * @returns {Promise<Object>} Response result
 */
export const respondToAccessRequest = async (requestId, action, responderId, ownerResponse = '') => {
  if (!requestId || !action || !responderId) {
    throw new Error('requestId, action, and responderId are required');
  }

  if (!['approve', 'deny'].includes(action)) {
    throw new Error('Action must be "approve" or "deny"');
  }

  try {
    // Get request
    const requestRef = doc(db, 'accessRequests', requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Request not found');
    }

    const request = requestDoc.data();

    // Verify responder is the owner
    if (request.ownerId !== responderId) {
      throw new Error('Only the job owner can respond to this request');
    }

    // Check if already responded
    if (request.status !== 'pending') {
      throw new Error(`Request has already been ${request.status}`);
    }

    // Check if expired
    if (request.expiresAt && request.expiresAt.toDate() < new Date()) {
      throw new Error('This request has expired');
    }

    let grantId = null;

    // If approving, create access grant
    if (action === 'approve') {
      try {
        // Get job and casefile details for the grant
        const jobDoc = await getDoc(doc(db, 'jobs', request.jobId));
        const casefileDoc = await getDoc(doc(db, 'casefiles', request.casefileId));
        
        if (!jobDoc.exists() || !casefileDoc.exists()) {
          throw new Error('Job or casefile not found');
        }

        const casefile = casefileDoc.data();

        grantId = await createAccessGrant({
          userId: request.requesterId,
          jobId: request.jobId,
          evidenceId: request.evidenceId,
          casefileId: request.casefileId,
          grantType: 'request_approved',
          requestId: requestId,
          approvedBy: responderId,
          casefileName: casefile.title || casefile.name || 'Private Investigation',
          casefileType: casefile.isPublic ? 'public' : 'private',
          showCasefileName: casefile.showNameToViewers || false
        });

        console.log(`✅ Access grant created: ${grantId}`);
      } catch (grantError) {
        console.error('Error creating grant:', grantError);
        throw new Error(`Failed to create access grant: ${grantError.message}`);
      }
    }

    // Update request status
    await updateDoc(requestRef, {
      status: action === 'approve' ? 'approved' : 'denied',
      respondedAt: serverTimestamp(),
      ownerResponse: ownerResponse.trim() || null,
      grantId: grantId
    });

    console.log(`✅ Request ${action === 'approve' ? 'approved' : 'denied'}: ${requestId}`);

    return {
      success: true,
      action,
      requestId,
      grantId,
      message: action === 'approve' ? 'Access granted successfully' : 'Request denied'
    };

  } catch (error) {
    console.error('Error responding to access request:', error);
    throw error;
  }
};

/**
 * Get pending requests for an owner
 * 
 * @param {string} ownerId - Owner ID
 * @returns {Promise<Array>} List of pending requests
 */
export const getOwnerPendingRequests = async (ownerId) => {
  if (!ownerId) {
    throw new Error('ownerId is required');
  }

  try {
    const requestsQuery = query(
      collection(db, 'accessRequests'),
      where('ownerId', '==', ownerId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(requestsQuery);
    const requests = [];

    snapshot.forEach(doc => {
      const data = doc.data();
      
      // Filter out expired requests
      if (!data.expiresAt || data.expiresAt.toDate() > new Date()) {
        requests.push({
          id: doc.id,
          ...data
        });
      }
    });

    console.log(`Found ${requests.length} pending requests for owner ${ownerId}`);
    return requests;
  } catch (error) {
    console.error('Error getting owner requests:', error);
    throw error;
  }
};

/**
 * Get all requests for an owner (pending, approved, denied)
 * 
 * @param {string} ownerId - Owner ID
 * @param {number} [limit] - Max number of requests to return
 * @returns {Promise<Array>} List of requests
 */
export const getOwnerAllRequests = async (ownerId, limit = 50) => {
  if (!ownerId) {
    throw new Error('ownerId is required');
  }

  try {
    const requestsQuery = query(
      collection(db, 'accessRequests'),
      where('ownerId', '==', ownerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(requestsQuery);
    const requests = [];

    snapshot.forEach(doc => {
      if (requests.length < limit) {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      }
    });

    return requests;
  } catch (error) {
    console.error('Error getting all owner requests:', error);
    throw error;
  }
};

/**
 * Get user's request history
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of user's requests
 */
export const getUserRequests = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const requestsQuery = query(
      collection(db, 'accessRequests'),
      where('requesterId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(requestsQuery);
    const requests = [];

    snapshot.forEach(doc => {
      requests.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return requests;
  } catch (error) {
    console.error('Error getting user requests:', error);
    throw error;
  }
};

/**
 * Check if user has pending request for a job
 * 
 * @param {string} userId - User ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object|null>} Pending request or null
 */
export const checkPendingRequest = async (userId, jobId) => {
  if (!userId || !jobId) {
    return null;
  }

  try {
    const requestsQuery = query(
      collection(db, 'accessRequests'),
      where('requesterId', '==', userId),
      where('jobId', '==', jobId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(requestsQuery);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    // Check if expired
    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      return null;
    }

    return {
      id: doc.id,
      ...data
    };
  } catch (error) {
    console.error('Error checking pending request:', error);
    return null;
  }
};

/**
 * Get request statistics for owner
 * 
 * @param {string} ownerId - Owner ID
 * @returns {Promise<Object>} Request statistics
 */
export const getOwnerRequestStats = async (ownerId) => {
  if (!ownerId) {
    throw new Error('ownerId is required');
  }

  try {
    const allRequests = await getOwnerAllRequests(ownerId, 1000);

    const stats = {
      total: allRequests.length,
      pending: 0,
      approved: 0,
      denied: 0,
      approvalRate: 0
    };

    allRequests.forEach(request => {
      if (request.status === 'pending') stats.pending++;
      if (request.status === 'approved') stats.approved++;
      if (request.status === 'denied') stats.denied++;
    });

    const answered = stats.approved + stats.denied;
    if (answered > 0) {
      stats.approvalRate = Math.round((stats.approved / answered) * 100);
    }

    return stats;
  } catch (error) {
    console.error('Error getting owner request stats:', error);
    throw error;
  }
};

// Export all functions
export default {
  createAccessRequest,
  respondToAccessRequest,
  getOwnerPendingRequests,
  getOwnerAllRequests,
  getUserRequests,
  checkPendingRequest,
  getOwnerRequestStats
};

